import { batch } from 'react-redux';
import { AuthenticationResult } from '@azure/msal-browser';
import { AxiosError } from 'axios';

import { apiActions } from './api.actions';
import { ThunkActionCreator } from '..';
import {
  AggregatedJobsParams,
  AggregatedPogMetricsParams,
  AggregatedProductMetricsParams,
  AggregatedSolverOutputParams,
  DownloadAssortmentExcelFileParam,
  DownloadMyDataFileParam,
  DownloadOutputFileParam,
  DownloadPEGxFileParam,
  FlipPogParams,
  GetAssortmentParams,
  GetAvailableAislesParams,
  GetDefaultPogMetricsParams,
  GetDefaultProductMetricsParams,
  GetEditedJobParams,
  getFetchCombinedPogParams,
  getFetchPogByStoreParams,
  GetProductMetricsParams,
  getOptimizationJobParams,
  getPogMetricsParams,
  GetOptimizationStatusParams,
  PogSolverOutputParams,
  LaunchOptimizationJobParams,
  CopySharedPogParams,
  PushPogToStoreParams,
  SaveMyNotesParams,
  SaveNewEditedPogParams,
  SaveUserPreferenceParams,
  SetUserRoleParams,
  SeedsParams,
  UpdateSeedSharedParams,
  ApproveSeedParams,
  RemovePogParams,
  GetMerchGuideLinkParams,
  ChangeSharedPogNameParams,
  ChangePogNameParams,
  ChangePrivateAndSharedPogNamesParams,
  UpdateSeedOwnershipParams,
} from '../../types/requests';
import { CopySharedPogResponse, DownloadOutputFilesResponse, FlipPogResponse } from '../../types/responses';
import msalService from '../../services/msalService';
import { SetResizedPogParams, SetResizedPogRequestParams } from '../viewAndEdit/viewAndEdit.types';
import { editedSolverOutputSelector } from '../job/job.selectors';
import { UserRole } from '../../helpers/utils';
import { JobDeleteParams } from '../job/job.types';
import { trackSeedDownload } from '../../telemetry';

// Tracked here rather than on the four pages that dispatch it (Visual, Compare, Assortment, Customize).
const trackPushPogToStore = (
  { jobId, aisleCode, sourceStoreNum, targetStoreNum }: PushPogToStoreParams,
  success: boolean,
) =>
  trackSeedDownload({
    downloadType: 'PUSH_POG_TO_STORE',
    jobId,
    aisleCode,
    sourceStoreNum,
    destStoreNum: targetStoreNum,
    success,
  });

export const getAuth: ThunkActionCreator = () => async (
  dispatch,
  _,
  { apiClientJava }
) => {
  dispatch(apiActions.getAuth.request());

  try {
    const response = await apiClientJava.getAuth();

    dispatch(apiActions.getAuth.success(response));
  } catch (err) {
    dispatch(apiActions.getAuth.failure(err as AxiosError));
  }
};

export const setUserRole: ThunkActionCreator<UserRole[]> = (new_roles) => async (
  dispatch,
  getState,
  { apiClientJava }
) => {
  const username = getState().user.currentUser?.email ?? '';
  const params: SetUserRoleParams = {
    username,
    new_roles
  };

  dispatch(apiActions.setUserRole.request(params));

  try {
    await apiClientJava.setUserRole(params);
    dispatch(apiActions.setUserRole.success());
    window.location.reload();
  } catch (err) {
    dispatch(apiActions.setUserRole.failure(err as AxiosError));
  }
};

export const getDatasetInfo: ThunkActionCreator = () => async (
  dispatch,
  _,
  { apiClientJava }
) => {
  dispatch(apiActions.getDatasetInfo.request());

  try {
    const response = await apiClientJava.getDatasetInfo();

    dispatch(apiActions.getDatasetInfo.success(response));
  } catch (err) {
    dispatch(apiActions.getDatasetInfo.failure(err as AxiosError));
  }
};

export const getAvailableAisles: ThunkActionCreator<GetAvailableAislesParams> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  dispatch(apiActions.getAvailableAisles.request());

  try {
    const response = await apiClientJava.getAvailableAisles(params);

    dispatch(apiActions.getAvailableAisles.success(response));
  } catch (err) {
    dispatch(apiActions.getAvailableAisles.failure(err as AxiosError));
  }
};

export const getDefaultPogMetrics: ThunkActionCreator<GetDefaultPogMetricsParams> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  dispatch(apiActions.getDefaultPogMetrics.request());

  try {
    const response = await apiClientJava.getDefaultPogMetrics(params);

    dispatch(apiActions.getDefaultPogMetrics.success(response));
  } catch (err) {
    dispatch(apiActions.getDefaultPogMetrics.failure(err as AxiosError));
  }
};

export const getPogMetrics: ThunkActionCreator<getPogMetricsParams> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  dispatch(apiActions.getPogMetrics.request());

  try {
    const response = await apiClientJava.getPogMetrics(params);

    dispatch(apiActions.getPogMetrics.success({ ...response, jobId: params.jobId }));
  } catch (err) {
    dispatch(apiActions.getPogMetrics.failure(err as AxiosError));
  }
};

export const pushPogToStore: ThunkActionCreator<PushPogToStoreParams> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  dispatch(apiActions.pushPogToStore.request());

  try {
    const response = await apiClientJava.pushPogToStore(params);

    dispatch(apiActions.pushPogToStore.success(response));
    trackPushPogToStore(params, true);
  } catch (err) {
    dispatch(apiActions.pushPogToStore.failure(err as AxiosError));
    trackPushPogToStore(params, false);
  }
};

export const copySharedPog: ThunkActionCreator<CopySharedPogParams, Promise<CopySharedPogResponse | undefined>> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  dispatch(apiActions.copySharedPog.request());

  try {
    const response = await apiClientJava.copySharedPog(params);

    dispatch(apiActions.copySharedPog.success(response));

    return response;
  } catch (err) {
    dispatch(apiActions.copySharedPog.failure(err as AxiosError));

    return undefined;
  }
};

export const getDefaultProductMetrics: ThunkActionCreator<GetDefaultProductMetricsParams> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  dispatch(apiActions.getDefaultProductMetrics.request());

  try {
    const response = await apiClientJava.getDefaultProductMetrics(params);

    dispatch(apiActions.getDefaultProductMetrics.success(response));
  } catch (err) {
    dispatch(apiActions.getDefaultProductMetrics.failure(err as AxiosError));
  }
};

export const getCurrentStateProductsAssortment: ThunkActionCreator<GetDefaultPogMetricsParams> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  dispatch(apiActions.getCurrentStateProductsAssortment.request());

  try {
    const response = await apiClientJava.getDefaultProductMetrics(params);

    dispatch(apiActions.getCurrentStateProductsAssortment.success(response));
  } catch (err) {
    dispatch(apiActions.getCurrentStateProductsAssortment.failure(err as AxiosError));
  }
};

export const getParameters: ThunkActionCreator<GetAssortmentParams> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  dispatch(apiActions.getAssortmentParams.request());

  try {
    const response = await apiClientJava.getParameters(params);

    dispatch(apiActions.getAssortmentParams.success(response));
  } catch (err) {
    dispatch(apiActions.getAssortmentParams.failure(err as AxiosError));
  }
};

export const launchOptimizationJob: ThunkActionCreator<LaunchOptimizationJobParams> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  dispatch(apiActions.launchOptimizationJob.request());

  try {
    const response = await apiClientJava.launchOptimizationJob(params);

    dispatch(apiActions.launchOptimizationJob.success(response));
  } catch (err) {
    dispatch(apiActions.launchOptimizationJob.failure(err as AxiosError));
  }
};

export const getOptimizationStatus: ThunkActionCreator<GetOptimizationStatusParams> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  dispatch(apiActions.getOptimizationStatus.request());

  try {
    const response = await apiClientJava.getOptimizationStatus(params);

    dispatch(apiActions.getOptimizationStatus.success(response));
  } catch (err) {
    dispatch(apiActions.getOptimizationStatus.failure(err as AxiosError));
  }
};

export const getPogSolverOutput: ThunkActionCreator<PogSolverOutputParams> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  dispatch(apiActions.getPogSolverOutput.request(params));

  try {
    const response = await apiClientJava.getPogSolverOutput(params);

    dispatch(apiActions.getPogSolverOutput.success(response));
  } catch (err) {
    dispatch(apiActions.getPogSolverOutput.failure(err as AxiosError));
  }
};

export const getProductMetrics: ThunkActionCreator<GetProductMetricsParams> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  dispatch(apiActions.getProductMetrics.request());

  try {
    const response = await apiClientJava.getProductMetrics(params);

    dispatch(apiActions.getProductMetrics.success(response));
  } catch (err) {
    dispatch(apiActions.getProductMetrics.failure(err as AxiosError));
  }
};

export const setUserInfo: ThunkActionCreator<AuthenticationResult> = (
  userInfo: AuthenticationResult
) => async (dispatch, _, { apiClientJava }) => {
  const { name, preferred_username: email } = userInfo.idTokenClaims as {
    name: string;
    preferred_username: string;
  };

  dispatch(apiActions.setUserInfo.request());

  try {
    dispatch(apiActions.setUserInfo.success({ name, email }));
    apiClientJava.setAccessToken(userInfo.accessToken);
    msalService.setAccessToken(userInfo.accessToken);
  } catch (err) {
    dispatch(apiActions.setUserInfo.failure(err as AxiosError));
  }
};

export const serverSideLogout: ThunkActionCreator = () => async (
  dispatch,
  _,
  { apiClientJava }
) => {
  dispatch(apiActions.serverSideLogout.request());

  try {
    await apiClientJava.serverSideLogout();
    dispatch(apiActions.serverSideLogout.success());
  } catch (err) {
    dispatch(apiActions.serverSideLogout.failure(err as AxiosError));
  }
};

export const downloadOutputFiles: ThunkActionCreator<
  DownloadOutputFileParam,
  Promise<DownloadOutputFilesResponse | undefined>
> =
  (params) =>
    async (dispatch, _, { fetchClientJava }) => {
      dispatch(apiActions.downloadOutputFiles.request());

      try {
        const response = await fetchClientJava.downloadOutputFiles(params);
        const { includeImages } = params;
        const { status } = response;

        dispatch(apiActions.downloadOutputFiles.success(includeImages ? status : null));

        return response;
      } catch (err) {
        dispatch(apiActions.downloadOutputFiles.failure(err as AxiosError));

        return undefined;
      }
    };

export const downloadPEGxFile: ThunkActionCreator<DownloadPEGxFileParam, Promise<boolean>> = (params) =>
  async (dispatch, _, { fetchClientJava }) => {
    dispatch(apiActions.downloadPEGxFile.request());

    try {
      const opened = await fetchClientJava.downloadPEGxFile(params);

      dispatch(apiActions.downloadPEGxFile.success());

      return opened;
    } catch (err) {
      dispatch(apiActions.downloadPEGxFile.failure(err as AxiosError));

      return false;
    }
  };

export const downloadMyDataFile: ThunkActionCreator<DownloadMyDataFileParam> =
  (params) =>
    async (dispatch, _, { fetchClientJava }) => {
      dispatch(apiActions.downloadMyDataFile.request());

      // jobId is left out on purpose: the tracker context supplies it on EDITOR / REVIEW routes.
      const track = (success: boolean) => trackSeedDownload({
        downloadType: 'DATA_FILE',
        aisleCode: params.aisle_code,
        storeNum: params.store_num,
        success,
      });

      try {
        const response = await fetchClientJava.downloadMyDataFiles(params);

        track(Boolean(response));

        if (response) {
          dispatch(apiActions.downloadMyDataFile.success(response));
          params.onSuccess();
        } else {
          dispatch(apiActions.downloadMyDataFile.failure(new Error('Print Error') as AxiosError));
          params.onError();
        }
      } catch (err) {
        track(false);
        dispatch(apiActions.downloadMyDataFile.failure(err as AxiosError));
        params.onError();
      }
    };

export const downloadAssortmentExcelFile: ThunkActionCreator<DownloadAssortmentExcelFileParam> =
  (params) =>
    async (dispatch, _, { fetchClientJava }) => {
      dispatch(apiActions.downloadAssortmentExcelFile.request());

      try {
        const response = await fetchClientJava.downloadAssortmentExcelFile(params);

        dispatch(apiActions.downloadAssortmentExcelFile.success(response));
      } catch (err) {
        dispatch(apiActions.downloadAssortmentExcelFile.failure(err as AxiosError));
      }
    };

export const getOptimizationJobLimit: ThunkActionCreator<getOptimizationJobParams> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  dispatch(apiActions.getOptimizationJobLimit.request());

  try {
    const response = await apiClientJava.getOptimizationJobLimit(params);

    dispatch(apiActions.getOptimizationJobLimit.success(response));
  } catch (err) {
    dispatch(apiActions.getOptimizationJobLimit.failure(err as AxiosError));
  }
};

export const getEditedJobs: ThunkActionCreator<GetEditedJobParams> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  try {
    dispatch(apiActions.getEditedJobs.request());
    const response = await apiClientJava.getEditedJobs(params);

    dispatch(apiActions.getEditedJobs.success(response));
  } catch (err) {
    dispatch(apiActions.getEditedJobs.failure(err as AxiosError));
  }
};

export const saveNewEditedPog: ThunkActionCreator<SaveNewEditedPogParams> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  try {
    const { storeNumber, aisleCode, jobId } = params;

    dispatch(apiActions.saveNewEditedPog.request());
    const response = await apiClientJava.saveNewEditedPog(params);
    const { job_id: responseJobId } = response.job;

    if (jobId) {
      batch(() => {
        dispatch(apiActions.saveNewEditedPog.success(response));
        dispatch(getPogMetrics({ storeNumber, aisleCode, jobId: responseJobId }));
        dispatch(getProductMetrics({ storeNumber, aisleCode, jobId: responseJobId }));
      });
    }
  } catch (err) {
    dispatch(apiActions.saveNewEditedPog.failure(err as AxiosError));
  }
};

export const updateEditedPog: ThunkActionCreator<SaveNewEditedPogParams> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  try {
    const { storeNumber, aisleCode, jobId } = params;

    dispatch(apiActions.updateEditedPog.request());
    const response = await apiClientJava.updateEditedPog(params);

    if (jobId) {
      batch(() => {
        dispatch(apiActions.updateEditedPog.success(response));
        dispatch(getPogMetrics({ storeNumber, aisleCode, jobId }));
        dispatch(getProductMetrics({ storeNumber, aisleCode, jobId }));
      });
    }
  } catch (err) {
    dispatch(apiActions.updateEditedPog.failure(err as AxiosError));
  }
};

export const getFetchPogByStore: ThunkActionCreator<getFetchPogByStoreParams> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  dispatch(apiActions.getFetchPogByStore.request());

  try {
    const response = await apiClientJava.getFetchPogByStore(params);

    dispatch(apiActions.getFetchPogByStore.success(response));
  } catch (err) {
    dispatch(apiActions.getFetchPogByStore.failure(err as AxiosError));
  }
};

export const getFetchCombinedPog: ThunkActionCreator<getFetchCombinedPogParams> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  dispatch(apiActions.getFetchCombinedPog.request());

  try {
    const response = await apiClientJava.getFetchCombinedPog(params);

    dispatch(apiActions.getFetchCombinedPog.success(response));
  } catch (err) {
    dispatch(apiActions.getFetchCombinedPog.failure(err as AxiosError));
  }
};

export const aggregatedJobs: ThunkActionCreator<AggregatedJobsParams> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  dispatch(apiActions.aggregatedJobs.request());

  try {
    const response = await apiClientJava.aggregatedJobs(params);

    dispatch(apiActions.aggregatedJobs.success(response));
  } catch (err) {
    dispatch(apiActions.aggregatedJobs.failure(err as AxiosError));
  }
};

// Loads jobs by explicit ids and merges them into the existing list. The plain aggregatedJobs call
// only returns the latest `limit` jobs, so a job_id coming from the URL (a deep link to an older
// POG) can be absent from it and has to be requested on its own.
export const aggregatedJobsById: ThunkActionCreator<AggregatedJobsParams> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  dispatch(apiActions.aggregatedJobsById.request());

  try {
    const response = await apiClientJava.aggregatedJobs(params);

    dispatch(apiActions.aggregatedJobsById.success({ jobIds: params.jobIds || [], jobs: response }));
  } catch (err) {
    dispatch(apiActions.aggregatedJobsById.failure(err as AxiosError));
  }
};

export const aggregatedSolverOutput: ThunkActionCreator<AggregatedSolverOutputParams> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  dispatch(apiActions.aggregatedSolverOutput.request(params));

  try {
    const response = await apiClientJava.aggregatedSolverOutput(params);

    dispatch(apiActions.aggregatedSolverOutput.success(response));
  } catch (err) {
    dispatch(apiActions.aggregatedSolverOutput.failure(err as AxiosError));
  }
};

export const aggregatedProductMetrics: ThunkActionCreator<AggregatedProductMetricsParams> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  dispatch(apiActions.aggregatedProductMetrics.request(params));

  try {
    const response = await apiClientJava.aggregatedProductMetrics(params);

    dispatch(apiActions.aggregatedProductMetrics.success(response));
  } catch (err) {
    dispatch(apiActions.aggregatedProductMetrics.failure(err as AxiosError));
  }
};

export const aggregatedPogMetrics: ThunkActionCreator<AggregatedPogMetricsParams> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  dispatch(apiActions.aggregatedPogMetrics.request(params));

  try {
    const response = await apiClientJava.aggregatedPogMetrics(params);

    dispatch(apiActions.aggregatedPogMetrics.success(response));
  } catch (err) {
    dispatch(apiActions.aggregatedPogMetrics.failure(err as AxiosError));
  }
};

export const getUserPreference: ThunkActionCreator = () => async (
  dispatch,
  _,
  { apiClientJava }
) => {
  dispatch(apiActions.getUserPreference.request());

  try {
    const response = await apiClientJava.getUserPreference();

    dispatch(apiActions.getUserPreference.success(response));
  } catch (err) {
    dispatch(apiActions.getUserPreference.failure(err as AxiosError));
  }
};

export const saveUserPreference: ThunkActionCreator<SaveUserPreferenceParams> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  dispatch(apiActions.saveUserPreference.request(params));

  try {
    const response = await apiClientJava.saveUserPreference(params);

    dispatch(apiActions.saveUserPreference.success(response));
  } catch (err) {
    dispatch(apiActions.saveUserPreference.failure(err as AxiosError));
  }
};

export const saveMyNotes: ThunkActionCreator<SaveMyNotesParams> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  dispatch(apiActions.saveMyNotes.request());

  try {
    const response = await apiClientJava.saveMyNotes(params);

    dispatch(apiActions.saveMyNotes.success(response));
  } catch (err) {
    dispatch(apiActions.saveMyNotes.failure(err as AxiosError));
  }
};

export const flipPog: ThunkActionCreator<FlipPogParams, Promise<FlipPogResponse | undefined>> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  dispatch(apiActions.flipPog.request());

  try {
    const response = await apiClientJava.flipPog(params);

    dispatch(apiActions.flipPog.success(response));

    return response;
  } catch (err) {
    dispatch(apiActions.flipPog.failure(err as AxiosError));

    return undefined;
  }
};

export const setResizedPog: ThunkActionCreator<SetResizedPogParams> = (
  {
    blocks,
    storeNumber,
    aisleCode,
  }
) => async (dispatch, getState, { apiClientJava }) => {
  const state = getState();

  try {
    const editedSolverOutput = editedSolverOutputSelector(state);

    const data: SetResizedPogRequestParams = {
      store_num: storeNumber,
      aisle_code: aisleCode,
      job_id: editedSolverOutput.job_id,
      facing_groups: editedSolverOutput.facing_groups,
      blocks: editedSolverOutput.blocks,
      new_aisle_geometry: blocks,
      clipboard: editedSolverOutput.clipboard,
      clipboardFixtures: editedSolverOutput.clipboardFixtures,
      clipboardFixturesFacings: editedSolverOutput.clipboardFixturesFacings,
      located_fixtures: editedSolverOutput.located_fixtures,
      type: editedSolverOutput.type,
      workbench: editedSolverOutput.workbench,
    };

    dispatch(apiActions.setResizedPog.request(data));

    const response = await apiClientJava.setResizedPog(data);

    batch(
      () => {
        dispatch(apiActions.setResizedPog.success(response));
        const commonParams = {
          storeNumber,
          aisleCode,
          jobId: editedSolverOutput.job_id,
          reqBody: {
            ...data,
            blocks: response.blocks,
            jobId: response.job_id,
            ...response.planogram
          },
        };

        dispatch(saveNewEditedPog(commonParams));
      });

  } catch (err) {
    dispatch(apiActions.setResizedPog.failure(err as AxiosError));
  }
};

export const deletePogJob: ThunkActionCreator<JobDeleteParams> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  dispatch(apiActions.deletePogJob.request(params));

  try {
    await apiClientJava.deletePogJob(params);

    dispatch(apiActions.deletePogJob.success(params)); // Returning params to have deleted job id for state update
  } catch (err) {
    dispatch(apiActions.deletePogJob.failure(err as AxiosError));
  }
};

export const removePog: ThunkActionCreator<RemovePogParams> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  dispatch(apiActions.removePog.request(params));

  try {
    await apiClientJava.removePog(params);

    dispatch(apiActions.removePog.success(params));
  } catch (err) {
    dispatch(apiActions.removePog.failure(err as AxiosError));
  }
};

export const seeds: ThunkActionCreator<SeedsParams> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  dispatch(apiActions.seeds.request(params));

  try {
    const response = await apiClientJava.seeds(params);

    dispatch(apiActions.seeds.success(response));
  } catch (err) {
    dispatch(apiActions.seeds.failure(err as AxiosError));
  }
};

export const updateSeedShared: ThunkActionCreator<UpdateSeedSharedParams> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  dispatch(apiActions.updateSeedShared.request(params));

  try {
    await apiClientJava.updateSeedShared(params);

    dispatch(apiActions.updateSeedShared.success(params));
  } catch (err) {
    dispatch(apiActions.updateSeedShared.failure(err as AxiosError));
  }
};

export const approveSeed: ThunkActionCreator<ApproveSeedParams> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  dispatch(apiActions.approveSeed.request(params));

  try {
    await apiClientJava.approveSeed(params);

    dispatch(apiActions.approveSeed.success(params));
  } catch (err) {
    dispatch(apiActions.approveSeed.failure(err as AxiosError));
  }
};

export const getMerchGuideLink: ThunkActionCreator<GetMerchGuideLinkParams> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  dispatch(apiActions.getMerchGuideLink.request(params));

  try {
    const response = await apiClientJava.getMerchGuideLink(params);

    dispatch(apiActions.getMerchGuideLink.success(response));
  } catch (err) {
    dispatch(apiActions.getMerchGuideLink.failure(err as AxiosError));
  }
};

export const changeSharedPogName: ThunkActionCreator<ChangeSharedPogNameParams> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  dispatch(apiActions.changeSharedPogName.request(params));

  try {
    await apiClientJava.changeSharedPogName(params);

    dispatch(apiActions.changeSharedPogName.success(params));
  } catch (err) {
    dispatch(apiActions.changeSharedPogName.failure(err as AxiosError));
  }
};

export const changePogName: ThunkActionCreator<ChangePogNameParams> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  dispatch(apiActions.changePogName.request(params));

  try {
    await apiClientJava.changePogName(params);

    dispatch(apiActions.changePogName.success(params));
  } catch (err) {
    dispatch(apiActions.changePogName.failure(err as AxiosError));
  }
};

export const changePrivateAndSharedPogNames: ThunkActionCreator<ChangePrivateAndSharedPogNamesParams> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  dispatch(apiActions.changePrivateAndSharedPogNames.request(params));

  try {
    await apiClientJava.changePrivateAndSharedPogNames(params);

    dispatch(apiActions.changePrivateAndSharedPogNames.success(params));
  } catch (err) {
    dispatch(apiActions.changePrivateAndSharedPogNames.failure(err as AxiosError));
  }
};

export const updateSeedOwnership: ThunkActionCreator<UpdateSeedOwnershipParams> = (
  params
) => async (dispatch, _, { apiClientJava }) => {
  dispatch(apiActions.updateSeedOwnership.request(params));

  try {
    await apiClientJava.updateSeedOwnership(params);

    dispatch(apiActions.updateSeedOwnership.success(params));
  } catch (err) {
    dispatch(apiActions.updateSeedOwnership.failure(err as AxiosError));
  }
};